import { useCallback, useEffect, useRef, useState } from 'react'
import { cabinetApi, type BBox, type PrepareResponse } from '../api'
import { t } from '../i18n'
import { resolveLoadFlow } from './loadFlow'
import { isQueuedStatus } from './loadState'
import { pollFleetRefresh } from './fleetRefresh'
import {
  CARD_RESULT_MS, type CardProgress, isSuperseded, pollCardRefresh, shouldStartCardPoll,
  toCardProgress,
} from './cardRefresh'

export type LoadState =
  | 'idle'
  | 'preparing'
  | 'manifest'
  | 'queued'
  | 'downloading'
  | 'ready'
  | 'failed'

export interface LoadParams {
  bbox: BBox
  date: string
  layers: string[]
  species_id?: number | null
  depth?: number
  kind?: 'raw' | 'model' | 'upwelling'
  /** Шаблон тайлов, который показать по готовности. */
  tileUrl: string
}

export function useLoadData(
  onReady: (tileUrl: string) => void,
  /** Вызывается, когда фоновое обновление позиций судов закончилось: страница
   *  по этому сигналу один раз перезапрашивает слой флота. Обновление идёт
   *  отдельно от океанографии и обычно заканчивается позже неё. */
  onFleetRefreshed?: () => void,
) {
  const [state, setState] = useState<LoadState>('idle')
  const [progress, setProgress] = useState(0)
  /** Прогресс точечного добора позиций судов; null — показывать нечего. Живёт
   *  отдельно от `progress` океанографии: это две разные работы, и добор обычно
   *  заканчивается заметно позже. */
  const [cardProgress, setCardProgress] = useState<CardProgress | null>(null)
  const cardHideRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Задание, чью полоску сейчас показываем. Опрос прежнего задания может
   *  доживать своё — его ответы на экран больше не пишутся. */
  const cardJobRef = useRef<number | null>(null)
  const [manifest, setManifest] = useState<PrepareResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pending = useRef<LoadParams | null>(null)
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      if (cardHideRef.current) clearTimeout(cardHideRef.current)
    }
  }, [])

  const poll = useCallback(
    async (taskId: string, tileUrl: string) => {
      // Задача создана, но воркер её ещё не взял — это очередь, а не загрузка.
      // «Загрузка 0%» здесь уверяла бы, что качаем, когда не качаем (см.
      // loadState.ts).
      setState('queued')
      setProgress(0)
      // eslint-disable-next-line no-constant-condition
      while (aliveRef.current) {
        await new Promise(r => setTimeout(r, 1500))
        if (!aliveRef.current) return
        try {
          const p = await cabinetApi.progress(taskId)
          if (!aliveRef.current) return
          setProgress(p.progress_percent)
          setState(isQueuedStatus(p.status) ? 'queued' : 'downloading')
          if (p.status === 'completed') {
            setState('ready')
            onReady(tileUrl)
            return
          }
          if (p.status === 'failed' || p.status === 'data_unavailable') {
            setError(
          p.error_message ||
            (p.status === 'data_unavailable' ? t('map.load.noFreshData') : t('map.load.error')),
        )
            setState('failed')
            return
          }
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e))
          setState('failed')
          return
        }
      }
    },
    [onReady],
  )

  const doConfirm = useCallback(
    async (taskId: string, tileUrl: string) => {
      try {
        await cabinetApi.confirm(taskId)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
        setState('failed')
        return
      }
      void poll(taskId, tileUrl)
    },
    [poll],
  )

  const run = useCallback(
    async (params: LoadParams, opts?: { refresh?: boolean }) => {
      setError(null)
      setState('preparing')
      pending.current = params
      let resp: PrepareResponse
      try {
        resp = await cabinetApi.prepare({
          bbox: params.bbox,
          date: params.date,
          layers: params.layers,
          species_id: params.species_id,
          depth: params.depth,
          kind: params.kind,
        })
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
        setState('failed')
        return
      }
      setManifest(resp)
      // Позиции судов обновляются фоном и кнопку не задерживают: опрашиваем
      // статус в стороне и по готовности один раз дёргаем слой флота.
      // fleet_job_id пуст, когда все квадраты района свежее 3 часов.
      void pollFleetRefresh(resp.fleet_job_id, {
        fetchStatus: cabinetApi.fleetRefreshStatus,
        onDone: () => {
          if (aliveRef.current) onFleetRefreshed?.()
        },
      })
      // Точечный добор по карточкам — второй, медленный слой (минуты вместо
      // секунд). Слой флота дёргаем на КАЖДОМ опросе, а не по готовности: суда
      // должны переезжать по мере обновления, иначе карта стоит неподвижно всю
      // работу.
      //
      // Строку показываем СРАЗУ, не дожидаясь первого опроса: он приходит через
      // 5 секунд, и всё это время экран выглядел бы так, будто клик не сработал.
      // По завершении строку не гасим мгновенно, а держим CARD_RESULT_MS с
      // итогом — исчезнувшая без следа полоска уже была принята за поломку.
      //
      // Повтор того же кадра присоединяется к ОТКРЫТОМУ заданию (сервер отвечает
      // тем же id и `card_job_joined`): его полоска уже на экране и опрашивается,
      // второй опрос того же задания ничего к ней не добавит.
      const jobId = resp.card_job_id ?? null
      if (shouldStartCardPoll(resp, cardJobRef.current)) {
        if (cardHideRef.current) clearTimeout(cardHideRef.current)
        cardJobRef.current = jobId
        setCardProgress(
          jobId
            ? { processed: 0, total: 0, updated: 0, done: false, error: null }
            : null,
        )
        void pollCardRefresh(jobId, {
          fetchStatus: cabinetApi.cardJobStatus,
          onProgress: s => {
            // Ответы заменённого или прежнего задания на экран не пишутся: полоска
            // принадлежит последнему нажатию.
            if (!aliveRef.current || cardJobRef.current !== jobId) return
            if (isSuperseded(s)) {
              // Заменено более новым нажатием того же человека (другая вкладка).
              // Молча: полоска нового задания живёт там, где нажали.
              cardJobRef.current = null
              setCardProgress(null)
              return
            }
            setCardProgress(toCardProgress(s))
            onFleetRefreshed?.()
            if (s.done) {
              cardJobRef.current = null
              cardHideRef.current = setTimeout(() => {
                if (aliveRef.current) setCardProgress(null)
              }, CARD_RESULT_MS)
            }
          },
        })
      }
      // Данные уже есть ИЛИ это «Обновить» (refresh) — пропускаем модалку и сразу
      // гоним докачку/пересчёт/тайлы. Модалку показываем только на ПЕРВОЙ загрузке
      // с недостающими данными.
      const flow = resolveLoadFlow({ refresh: opts?.refresh ?? false, needsDownload: resp.needs_download })
      if (flow === 'auto-confirm') {
        await doConfirm(resp.task_id, params.tileUrl)
      } else {
        setState('manifest')
      }
    },
    [doConfirm, onFleetRefreshed],
  )

  const confirm = useCallback(async () => {
    if (!manifest || !pending.current) return
    await doConfirm(manifest.task_id, pending.current.tileUrl)
  }, [manifest, doConfirm])

  const cancel = useCallback(() => {
    setState('idle')
    setManifest(null)
    pending.current = null
  }, [])

  return { state, progress, cardProgress, manifest, error, run, confirm, cancel }
}
